import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { identityLogin } from '@/store/identitySlice';
import { getActivePortalHomePath } from '@/store/identitySlice';
import { identityApi, identitySsoAuthorizeUrl } from '@/api/identity.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function IdentityLoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const loading = useAppSelector((s) => s.identity.loading);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [magicLinkMode, setMagicLinkMode] = useState(false);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await dispatch(
        identityLogin({ identifier, password, totpCode: mfaRequired ? totpCode : undefined }),
      ).unwrap();
      if (result.mfaRequired) {
        setMfaRequired(true);
        return;
      }
      if ((result.availablePortals?.length ?? 0) > 1) {
        navigate('/identity/portals', { replace: true });
        return;
      }
      navigate(getActivePortalHomePath({
        identityId: result.identityId,
        identityUuid: result.identityUuid,
        email: result.email,
        activeProfileId: result.activeProfileId!,
        activePortalCode: result.activePortalCode!,
        organizationId: result.organizationId!,
        organizationName: result.organizationName,
        permissions: result.permissions ?? [],
        availablePortals: result.availablePortals ?? [],
      }), { replace: true });
    } catch (err) {
      showApiError(err, 'Sign in failed');
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await identityApi.requestMagicLink(identifier);
      notify.success('Check your email for a sign-in link');
    } catch (err) {
      showApiError(err, 'Could not send sign-in link');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page dark:bg-slate-950 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="text-sm font-medium text-primary-600 dark:text-primary-400">Fattah One</p>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-2">Portal Sign In</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            One identity. Customer, vendor, employee, and more.
          </p>
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
            <button
              type="button"
              onClick={() => setMagicLinkMode(false)}
              className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                !magicLinkMode ? 'bg-white shadow dark:bg-slate-900' : 'text-slate-500'
              }`}
            >
              Password
            </button>
            <button
              type="button"
              onClick={() => setMagicLinkMode(true)}
              className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                magicLinkMode ? 'bg-white shadow dark:bg-slate-900' : 'text-slate-500'
              }`}
            >
              Magic link
            </button>
          </div>

          <form onSubmit={magicLinkMode ? handleMagicLink : handlePasswordLogin} className="space-y-4">
            <Input
              label="Email or mobile"
              type="text"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
            />
            {!magicLinkMode && (
              <>
                <Input
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required={!mfaRequired}
                />
                {mfaRequired && (
                  <Input
                    label="Authenticator code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    required
                  />
                )}
              </>
            )}
            <Button type="submit" loading={loading} className="w-full">
              {magicLinkMode ? 'Send sign-in link' : mfaRequired ? 'Verify & sign in' : 'Sign in'}
            </Button>
          </form>

          {!magicLinkMode && (
            <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              <p className="text-xs text-center text-slate-500 dark:text-slate-400">Or continue with</p>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="secondary" className="w-full" onClick={() => {
                  window.location.href = identitySsoAuthorizeUrl('google');
                }}>
                  Google
                </Button>
                <Button type="button" variant="secondary" className="w-full" onClick={() => {
                  window.location.href = identitySsoAuthorizeUrl('microsoft');
                }}>
                  Microsoft
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
          ERP staff?{' '}
          <Link to="/" className="font-medium text-primary-600 hover:underline dark:text-primary-400">
            Organization sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
